import React, { useCallback, useEffect, useState } from 'react';
import { Box, Chip, Container, Grid, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { AuditionCard } from './AuditionCard';
import { IAudition, IRole } from '../../types/global/helper';
import { AlternateAuditionCard } from './AlternateAuditionCard';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import FilterIcon from '../CustomSVGIcons/FilterIcon';
import { ReusableDrawer } from '../ReusableDrawer/ReusableDrawer';
import { AuditionWrapper } from './AuditionWrapper';
import { IAuditionFilter } from '../../redux/audition/types';
import { isString } from 'lodash';
import { useDispatch } from 'react-redux';
import { setAudition, setRole } from '../../redux/audition/actions';
import { isEmpty } from '../../utils/lodash';
import { EmptySearchBlock } from '../EsSidebar/EsSearch/EmptySearchBlock';
import { IUserProfile } from '../../redux/user/actions';

const niceColors = require('nice-color-palettes');

export interface IAuditionGrid {
    auditions: IAudition[];
    user?: IUserProfile;
    selectedFilters?: IAuditionFilter;
    deleteFilter?: (filter?: IAuditionFilter) => void;
    onUpdateFilter?: (filter: any) => void;
    onApplyClick: (role: IRole) => void;
    toggleFilterInMobile?: VoidFunction;
    totalResults?: number;
}
/**
 *
 * @param param0
 * @returns
 * total
 * location
 * type
 * kind
 *
 */

const EXCLUDED_KEYS = ['min_age', 'job_start_date'];

export const AuditionGrid: React.FC<IAuditionGrid> = ({
    auditions,
    selectedFilters,
    deleteFilter,
    onUpdateFilter,
    totalResults = 0,
    toggleFilterInMobile,
}) => {
    const [filter, setFilter] = useState(['total']);
    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
    const [selectedAudition, setSelectedAudition] = useState<IAudition | null>(null);
    const [open, setOpen] = useState(false);
    const colorsNice = [...niceColors[67], ...niceColors[1], ...niceColors[29], ...niceColors[71], ...niceColors[81]];
    const dispatch = useDispatch();

    const { themeStyle } = useThemeContext();
    const handleOnViewAndApplyClick = (audition: IAudition) => {
        setSelectedAudition(audition);
        setOpen(!open);
        dispatch(setAudition(audition));
    };
    const handleOnApplyClicked = (role: IRole, audition?: IAudition) => {
        dispatch(setRole(role));
        dispatch(setAudition(audition));
    };
    const handleClose = () => {
        setOpen(false);
        setSelectedAudition(null);
        dispatch(setAudition(undefined));
    };

    const handleDelete = (label: string) => {
        if (!selectedFilters) {
            return;
        }
        if (label && label.toLowerCase() === 'total') {
            return selectedFilters;
        }

        const updatedFilters = Object.keys(selectedFilters || {}).reduce((acc, key) => {
            const filter = selectedFilters[key as keyof IAuditionFilter];
            const { displayValue, value } = filter;

            if (Array.isArray(displayValue) && Array.isArray(value)) {
                const filteredDisplayValue = displayValue.filter(item => item.toLowerCase() !== label.toLowerCase());
                const filteredValue = value.filter(item => item.toLowerCase() !== label.toLowerCase());

                if (filteredDisplayValue.length > 0) {
                    acc[key as keyof IAuditionFilter] = {
                        ...filter,
                        displayValue: filteredDisplayValue,
                        value: filteredValue,
                    };
                }
            } else if (
                isString(displayValue) &&
                isString(value) &&
                displayValue.toLowerCase() !== label.toLowerCase() &&
                value.toLowerCase() !== label.toLowerCase() &&
                !EXCLUDED_KEYS.includes(key)
            ) {
                acc[key as keyof IAuditionFilter] = {
                    ...filter,
                    displayValue,
                    value,
                };
            }

            return acc;
        }, {} as typeof selectedFilters);

        onUpdateFilter && onUpdateFilter(updatedFilters);
        deleteFilter && deleteFilter(updatedFilters);
        return updatedFilters;
    };

    const extractFilters = useCallback(() => {
        const result: any[] = [];
        selectedFilters &&
            Object.keys(selectedFilters).forEach(key => {
                if (!EXCLUDED_KEYS.includes(key)) {
                    const value = selectedFilters[key as keyof IAuditionFilter].displayValue;
                    if (Array.isArray(value)) {
                        result.push(...value.filter(item => typeof item === 'string')); // Ensure strings only
                    } else {
                        result.push(value);
                    }
                }
            });
        return result;
    }, [selectedFilters]);
    useEffect(() => {
        if (selectedFilters) {
            setFilter(prevState => {
                const updatedFilters = ['total', ...extractFilters()];
                return [...new Set(updatedFilters)];
            });
        } else {
            setFilter(['total']);
        }
    }, [selectedFilters, extractFilters]);

    return (
        <Stack>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography
                    variant="h1"
                    component="h2"
                    fontWeight="bold"
                    color={theme[themeStyle].tag_white}
                    fontSize={32}>
                    Open Calls
                </Typography>
                <Stack direction={'row'} alignItems={'center'} gap={2}>
                    {isMobile && (
                        <FilterIcon
                            height={'30px'}
                            width={'30px'}
                            stroke={theme[themeStyle].tag_white}
                            onClick={toggleFilterInMobile}
                        />
                    )}
                </Stack>
            </Stack>

            <ReusableDrawer
                open={open}
                onClose={handleClose}
                anchor="right"
                content={
                    !!selectedAudition && (
                        <Container maxWidth={false} sx={{ height: '100vh', overflowY: 'auto', padding: 2 }}>
                            <AuditionWrapper audition={selectedAudition} disableSuggestion />
                        </Container>
                    )
                }
            />
            <Box
                display="flex"
                flexWrap="wrap"
                gap={1} // Adds spacing between items
                mb={1}>
                {filter.map((label, index) => (
                    <Chip
                        key={index}
                        label={label === 'total' ? `${label} (${totalResults})` : label}
                        onDelete={() => handleDelete(label)}
                        sx={{
                            textTransform: 'capitalize',
                            backgroundColor: colorsNice[index],
                            color: theme[themeStyle].true_black,
                        }}
                    />
                ))}
            </Box>

            <Grid
                container
                spacing={2}
                mr={{
                    lg: 0,
                }}>
                {isEmpty(auditions) && <EmptySearchBlock />}
                {!isEmpty(auditions) &&
                    auditions.map((audition, index) => (
                        <Grid item xs={12} key={index}>
                            {isMobile ? (
                                <AuditionCard
                                    audition={audition}
                                    onViewAndApplyClick={() => handleOnViewAndApplyClick(audition)}
                                />
                            ) : (
                                <AlternateAuditionCard
                                    audition={audition}
                                    onApplyClicked={handleOnApplyClicked}
                                    onViewAndApplyClick={() => handleOnViewAndApplyClick(audition)}
                                />
                            )}
                        </Grid>
                    ))}
            </Grid>
        </Stack>
    );
};
